import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowsUpDownIcon, LockClosedIcon, InformationCircleIcon, ArrowDownTrayIcon, PlusIcon, TrashIcon, ArrowLeftIcon, CloudArrowUpIcon } from '@heroicons/react/20/solid';
import DisclaimerFooter from './DisclaimerFooter';
import { signalDescriptions, rankDescriptions } from '../constants/signalDescriptions';

export default function WatchlistDetailPage() {
  const { watchlistId } = useParams();
  const navigate = useNavigate();
  const [watchlistData, setWatchlistData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // CSV Upload states
  const [showCsvModal, setShowCsvModal] = useState(false);
  const [, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState([]);
  const [csvHeaders, setCsvHeaders] = useState([]);
  const [selectedColumn, setSelectedColumn] = useState('');
  const [csvProcessing, setCsvProcessing] = useState(false);
  const [csvResults, setCsvResults] = useState(null);
  const [showCsvResults, setShowCsvResults] = useState(false);
  const [deleteAllLoading, setDeleteAllLoading] = useState(false);
  const [csvConfirmLoading, setCsvConfirmLoading] = useState(false);
  
  const [sortConfig, setSortConfig] = useState({
    key: 'rank_global',
    direction: 'asc'
  });
  const [activeTooltip, setActiveTooltip] = useState(null);
  
  const fetchWatchlistData = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/watchlists/${watchlistId}/rankings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setWatchlistData(response.data);
    } catch (error) {
      console.error('Error fetching watchlist data:', error);
      if (error.response?.status === 401) {
        navigate('/login');
      } else if (error.response?.status === 404) {
        navigate('/analyze');
      }
    } finally {
      setLoading(false);
    }
  }, [watchlistId, navigate]);

  useEffect(() => {
    fetchWatchlistData();
  }, [fetchWatchlistData]);

  const handleSecurityClick = (security) => {
    if (!security.is_masked) {
      navigate(`/ticker/${security.security_id}`, { 
        state: { security }
      });
    }
  };

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortedData = () => {
    if (!watchlistData?.rankings) return [];
    
    const sortableItems = [...watchlistData.rankings];
    
    sortableItems.sort((a, b) => {
      let aValue, bValue;
      
      if (sortConfig.key.includes('.')) {
        const [parent, child] = sortConfig.key.split('.');
        aValue = a[parent]?.[child] || 0;
        bValue = b[parent]?.[child] || 0;
      } else {
        aValue = a[sortConfig.key] || 0;
        bValue = b[sortConfig.key] || 0;
      }
      
      // Handle string sorting for ticker and company_name
      if (sortConfig.key === 'ticker' || sortConfig.key === 'company_name') {
        aValue = String(aValue || '').toLowerCase();
        bValue = String(bValue || '').toLowerCase();
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      }
      
      // Handle numeric sorting
      if (aValue < bValue) {
        return sortConfig.direction === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      
      return a.rank_global - b.rank_global;
    });
    
    return sortableItems;
  };

  const toggleTooltip = (factor) => {
    if (activeTooltip === factor) {
      setActiveTooltip(null);
    } else {
      setActiveTooltip(factor);
    }
  };

  const formatScore = (value, isMasked = false) => {
    if (isMasked) {
      return (
        <span className="inline-flex items-center rounded-full bg-bg-300/50 px-2 py-1 text-xs">
          <LockClosedIcon className="h-3 w-3 mr-1" />
        </span>
      );
    }
    
    if (value === null || value === undefined || value === '') {
      return <span className="text-text-300">–</span>;
    }
    
    return value;
  };

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 3) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/ea_stock_rankings/search?q=${encodeURIComponent(query)}`
      );
      setSearchResults(response.data.results || []);
    } catch (error) {
      console.error('Search error:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  const addToWatchlist = async (security) => {
    try {
      const token = localStorage.getItem('access_token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/watchlists/${watchlistId}/items`,
        { security_id: security.security_id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );
      
      // Refresh the watchlist data
      await fetchWatchlistData();
      setShowAddModal(false);
      setSearchQuery('');
      setSearchResults([]);
    } catch (error) {
      console.error('Error adding to watchlist:', error);
      alert(error.response?.data?.error || 'Failed to add security to watchlist');
    }
  };

  // CSV Upload Functions
  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      parseCsvFile(file);
    } else {
      alert('Please select a valid CSV file');
    }
  };

  const parseCsvFile = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      const lines = text.split('\n').filter(line => line.trim() !== '');
      
      if (lines.length < 2) {
        alert('CSV file must have at least a header row and one data row');
        return;
      }

      const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
      const data = lines.slice(1).map(line => {
        const values = line.split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        return row;
      });

      setCsvHeaders(headers);
      setCsvData(data);
      
      // Auto-detect ticker or CUSIP column
      const identifierColumn = headers.find(h => 
        h.toLowerCase().includes('ticker') || 
        h.toLowerCase().includes('symbol') || 
        h.toLowerCase() === 'tick' ||
        h.toLowerCase().includes('cusip')
      );
      if (identifierColumn) {
        setSelectedColumn(identifierColumn);
      }
    };
    reader.readAsText(file);
  };

  const processCsvUpload = async () => {
    if (!selectedColumn || csvData.length === 0) {
      alert('Please select a column and ensure CSV data is loaded');
      return;
    }

    setCsvProcessing(true);
    const results = {
      successful: [],
      failed: [],
      duplicates: []
    };

    // Note: We check for duplicates in the exact match logic below

    // Extract identifiers from selected column
    const identifiers = csvData
      .map(row => row[selectedColumn])
      .filter(identifier => identifier && identifier.trim() !== '')
      .map(identifier => identifier.trim());

    // Remove duplicates from the CSV itself
    const uniqueIdentifiers = [...new Set(identifiers)];

    try {
      // Search for each identifier (ticker or CUSIP)
      for (const identifier of uniqueIdentifiers) {
        try {
          const upperIdentifier = identifier.toUpperCase();
          
          // Check if already in watchlist (check both ticker and CUSIP)
          const isAlreadyInWatchlist = watchlistData?.rankings?.some(r => 
            r.ticker === upperIdentifier || r.cusip === identifier
          );
          
          if (isAlreadyInWatchlist) {
            results.duplicates.push({ ticker: identifier, reason: 'Already in watchlist' });
            continue;
          }

          // Search for the identifier using ticker-only search
          console.log(`Searching for ticker: ${identifier}`);
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/ea_stock_rankings/search?q=${encodeURIComponent(identifier)}&mode=tickers`
          );
          
          const searchResults = response.data.results || [];
          console.log(`Search results for ${identifier}:`, searchResults.length > 0 ? 
            searchResults.map(r => `${r.ticker} (${r.name})`) : 'No results');
          
          // Look for exact ticker match anywhere in the results
          let exactMatch = searchResults.find(r => r.ticker.toUpperCase() === upperIdentifier);
          
          if (exactMatch) {
            console.log(`✓ Found exact match for ${identifier}: ${exactMatch.ticker}`);
          } else if (searchResults.length > 0) {
            console.log(`✗ No exact match for ${upperIdentifier} in results:`, 
              searchResults.map(r => r.ticker).join(', '));
          } else {
            console.log(`✗ No search results returned for ${identifier}`);
          }
          
          if (exactMatch) {
            results.successful.push({
              ticker: identifier,
              security: exactMatch,
              name: exactMatch.name,
              sector: exactMatch.sector
            });
          } else {
            results.failed.push({ 
              ticker: identifier, 
              reason: `Not found (searched ${searchResults.length} results)`
            });
            // Debug logging for failed searches
            console.log(`Failed to find: ${identifier}, search returned:`, searchResults);
          }
        } catch (error) {
          console.error(`Search error for ${identifier}:`, error);
          results.failed.push({ ticker: identifier, reason: 'Search failed' });
        }
      }

      setCsvResults(results);
      setShowCsvResults(true);
    } catch (error) {
      console.error('Error processing CSV:', error);
      alert('Failed to process CSV file');
    } finally {
      setCsvProcessing(false);
    }
  };

  const confirmCsvUpload = async () => {
    if (!csvResults?.successful?.length) {
      alert('No securities to add');
      return;
    }

    setCsvConfirmLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Add all successful matches to watchlist
      for (const item of csvResults.successful) {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/watchlists/${watchlistId}/items`,
          { security_id: item.security.security_id },
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          }
        );
      }
      
      // Refresh the watchlist data
      await fetchWatchlistData();
      
      // Close modals and reset state
      closeCsvModal();
      
      alert(`Successfully added ${csvResults.successful.length} securities to watchlist`);
    } catch (error) {
      console.error('Error adding securities to watchlist:', error);
      alert('Failed to add some securities to watchlist');
    } finally {
      setCsvConfirmLoading(false);
    }
  };

  const closeCsvModal = () => {
    setShowCsvModal(false);
    setShowCsvResults(false);
    setCsvFile(null);
    setCsvData([]);
    setCsvHeaders([]);
    setSelectedColumn('');
    setCsvResults(null);
    setCsvProcessing(false);
  };

  const deleteAllFromWatchlist = async () => {
    const confirmMessage = `Are you sure you want to remove ALL ${sortedData.length} securities from this watchlist? This action cannot be undone.`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }

    setDeleteAllLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Delete all securities from the watchlist
      for (const security of watchlistData.rankings) {
        await axios.delete(
          `${process.env.REACT_APP_API_URL}/watchlists/${watchlistId}/items/${security.security_id}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
      }
      
      // Refresh the watchlist data
      await fetchWatchlistData();
      
      alert(`Successfully removed all securities from watchlist`);
    } catch (error) {
      console.error('Error deleting all securities:', error);
      alert('Failed to delete all securities from watchlist');
    } finally {
      setDeleteAllLoading(false);
    }
  };

  const removeFromWatchlist = async (securityId) => {
    if (!window.confirm('Are you sure you want to remove this security from the watchlist?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/watchlists/${watchlistId}/items/${securityId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      // Refresh the watchlist data
      await fetchWatchlistData();
    } catch (error) {
      console.error('Error removing from watchlist:', error);
      alert(error.response?.data?.error || 'Failed to remove security from watchlist');
    }
  };

  const exportToCSV = async () => {
    setExportLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      
      // Get the watchlist data
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/watchlists/${watchlistId}/rankings`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      const data = response.data.rankings;
      
      // Convert to CSV
      const csvContent = convertToCSV(data, response.data.is_approved);
      
      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      
      const filename = `${watchlistData.watchlist.name}_${new Date().toISOString().split('T')[0]}.csv`;
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    } finally {
      setExportLoading(false);
    }
  };

  const convertToCSV = (data, isApproved) => {
    if (!data || data.length === 0) return '';
    
    const headers = [
                              'Rank',
      'Ticker',
      'Company Name',
      'Sector',
      'Price',
      'Market Cap (M)',
      'Signal Score',
      'Value Score',
      'Momentum Score',
      'Quality Score',
      'Low Vol Score',
      'Earnings Score',
      'Transcripts Score',
      'Innovation Score',
      'Security ID',
      'As Of Date'
    ];
    
    const csvRows = [
      headers.join(','),
      ...data.map(stock => {
        const row = [
          stock.rank_global,
          `"${stock.ticker}"`,
          `"${stock.name.replace(/"/g, '""')}"`,
          `"${stock.sector || ''}"`,
          stock.price || '',
          stock.market_cap || '',
          stock.signal_weight || '',
          stock.component_scores.value || '',
          stock.component_scores.momentum || '',
          stock.component_scores.quality || '',
          stock.component_scores.lowvol || '',
          stock.component_scores.earnings || '',
          stock.component_scores.transcripts || '',
          stock.component_scores.innovation || '',
          stock.security_id,
          watchlistData.as_of_date
        ];
        return row.join(',');
      })
    ];
    
    return csvRows.join('\n');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bg-100 flex justify-center items-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-text-100"></div>
      </div>
    );
  }

  const sortedData = getSortedData();

  return (
    <div className="flex flex-col min-h-screen">
      <div className="flex-grow bg-bg-100 px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/analyze')}
            className="inline-flex items-center text-sm text-text-200 hover:text-text-100 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to Watchlists
          </button>
          
          <div className="sm:flex sm:items-center">
            <div className="sm:flex-auto">
              <h1 className="text-2xl font-semibold leading-6 text-text-100">
                {watchlistData?.watchlist?.name}
              </h1>
              {watchlistData?.watchlist?.description && (
                <p className="mt-2 text-sm text-text-200">
                  {watchlistData.watchlist.description}
                </p>
              )}
              <div className="mt-2 flex items-center space-x-4 text-sm text-text-300">
                <span>
                  {sortedData.length} {sortedData.length === 1 ? 'security' : 'securities'}
                </span>
              </div>
            </div>
            <div className="mt-4 sm:ml-16 sm:mt-0 sm:flex-none">
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={() => setShowAddModal(true)}
                  className="inline-flex items-center rounded-md bg-primary-100 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                >
                  <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                  Add Stock
                </button>
                <button
                  type="button"
                  onClick={() => setShowCsvModal(true)}
                  className="inline-flex items-center rounded-md bg-bg-300 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-bg-400"
                >
                  <CloudArrowUpIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                  Upload CSV
                </button>
                {sortedData.length > 0 && (
                  <button
                    type="button"
                    onClick={deleteAllFromWatchlist}
                    disabled={deleteAllLoading}
                    className="inline-flex items-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {deleteAllLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-1.5"></div>
                        Deleting...
                      </>
                    ) : (
                      <>
                        <TrashIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                        Delete All
                      </>
                    )}
                  </button>
                )}
                <button
                  type="button"
                  onClick={exportToCSV}
                  disabled={exportLoading}
                  className="inline-flex items-center rounded-md border border-white bg-transparent px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-white hover:text-bg-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exportLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-100 mr-1.5"></div>
                      Exporting...
                    </>
                  ) : (
                    <>
                      <ArrowDownTrayIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                      Export CSV
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Add Stock Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-bg-200 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-text-100 mb-4">Add Stock to Watchlist</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-100 mb-2">
                  Search for stocks
                </label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearch(e.target.value)}
                  placeholder="Search by ticker or company name..."
                  className="w-full bg-bg-300 text-text-100 rounded-lg border border-bg-400 px-3 py-2 focus:ring-primary-100 focus:border-primary-100"
                />
              </div>

              {searchLoading && (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-100 mx-auto"></div>
                </div>
              )}

              {searchResults.length > 0 && (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {searchResults.map((result) => (
                    <div
                      key={result.security_id}
                      className="flex items-center justify-between p-3 bg-bg-300 rounded-lg hover:bg-bg-400"
                    >
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-text-100">{result.ticker}</span>
                          <span className="text-xs text-text-300">#{result.rank_global}</span>
                        </div>
                        <div className="text-sm text-text-200">{result.name}</div>
                        <div className="text-xs text-text-300">{result.sector}</div>
                      </div>
                      <button
                        onClick={() => addToWatchlist(result)}
                        className="px-3 py-1 bg-primary-100 text-text-100 rounded text-sm hover:bg-primary-200"
                      >
                        Add
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {searchQuery.length >= 3 && searchResults.length === 0 && !searchLoading && (
                <div className="text-center py-4 text-text-300">
                  No stocks found for "{searchQuery}"
                </div>
              )}

              <div className="mt-6 flex justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddModal(false);
                    setSearchQuery('');
                    setSearchResults([]);
                  }}
                  className="px-4 py-2 text-sm font-medium text-text-200 hover:text-text-100"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSV Upload Modal */}
        {showCsvModal && !showCsvResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-bg-200 rounded-lg p-6 w-full max-w-2xl max-h-[80vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-text-100 mb-4">Upload CSV to Watchlist</h2>
              
              <div className="mb-4">
                <label className="block text-sm font-medium text-text-100 mb-2">
                  Select CSV File
                </label>
                <input
                  type="file"
                  accept=".csv"
                  onChange={handleCsvFileChange}
                  className="w-full bg-bg-300 text-text-100 rounded-lg border border-bg-400 px-3 py-2 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-100 file:text-text-100 hover:file:bg-primary-200"
                />
                <p className="text-xs text-text-300 mt-1">
                  CSV should contain a column with stock tickers, symbols, or CUSIPs
                </p>
              </div>

              {csvHeaders.length > 0 && (
                <div className="mb-4">
                                  <label className="block text-sm font-medium text-text-100 mb-2">
                  Select Ticker or CUSIP Column
                </label>
                  <select
                    value={selectedColumn}
                    onChange={(e) => setSelectedColumn(e.target.value)}
                    className="w-full bg-bg-300 text-text-100 rounded-lg border border-bg-400 px-3 py-2 focus:ring-primary-100 focus:border-primary-100"
                  >
                    <option value="">Select a column...</option>
                    {csvHeaders.map((header, index) => (
                      <option key={index} value={header}>
                        {header}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {csvData.length > 0 && selectedColumn && (
                <div className="mb-4">
                  <h3 className="text-sm font-medium text-text-100 mb-2">Preview</h3>
                  <div className="bg-bg-300 rounded-lg p-3 max-h-40 overflow-y-auto">
                    <div className="text-xs text-text-200">
                      Found {csvData.length} rows. Sample identifiers:
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {csvData.slice(0, 10).map((row, index) => (
                        <span key={index} className="bg-bg-400 px-2 py-1 rounded text-xs text-text-100">
                          {row[selectedColumn]}
                        </span>
                      ))}
                      {csvData.length > 10 && (
                        <span className="text-text-300 text-xs">...and {csvData.length - 10} more</span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={closeCsvModal}
                  className="px-4 py-2 text-sm font-medium text-text-200 hover:text-text-100"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={processCsvUpload}
                  disabled={!selectedColumn || csvData.length === 0 || csvProcessing}
                  className="px-4 py-2 bg-primary-100 text-text-100 rounded text-sm hover:bg-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {csvProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-100 mr-2 inline-block"></div>
                      Processing...
                    </>
                  ) : (
                    'Process CSV'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* CSV Results Modal */}
        {showCsvResults && csvResults && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-bg-200 rounded-lg p-6 w-full max-w-4xl max-h-[80vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-text-100 mb-4">CSV Upload Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-400/10 border border-green-400/20 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-green-400 mb-2">Successful</h3>
                  <div className="text-2xl font-bold text-green-400">{csvResults.successful.length}</div>
                  <div className="text-xs text-green-300">Securities found and ready to add</div>
                </div>
                <div className="bg-red-400/10 border border-red-400/20 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-red-400 mb-2">Failed</h3>
                  <div className="text-2xl font-bold text-red-400">{csvResults.failed.length}</div>
                  <div className="text-xs text-red-300">Tickers not found</div>
                </div>
                <div className="bg-yellow-400/10 border border-yellow-400/20 rounded-lg p-4">
                  <h3 className="text-sm font-medium text-yellow-400 mb-2">Duplicates</h3>
                  <div className="text-2xl font-bold text-yellow-400">{csvResults.duplicates.length}</div>
                  <div className="text-xs text-yellow-300">Already in watchlist</div>
                </div>
              </div>

              <div className="space-y-4">
                {csvResults.successful.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-green-400 mb-2">Securities to Add ({csvResults.successful.length})</h3>
                    <div className="bg-bg-300 rounded-lg p-3 max-h-40 overflow-y-auto">
                      <div className="space-y-1">
                        {csvResults.successful.map((item, index) => (
                          <div key={index} className="flex items-center justify-between text-sm">
                            <div className="flex items-center space-x-2">
                              <span className="font-medium text-text-100">{item.ticker}</span>
                              <span className="text-text-200">{item.name}</span>
                            </div>
                            <span className="text-xs text-text-300">{item.sector}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {csvResults.failed.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-red-400 mb-2">Failed to Find ({csvResults.failed.length})</h3>
                    <div className="bg-bg-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-1">
                        {csvResults.failed.map((item, index) => (
                          <span key={index} className="bg-red-400/20 text-red-300 px-2 py-1 rounded text-xs">
                            {item.ticker}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {csvResults.duplicates.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-yellow-400 mb-2">Duplicates ({csvResults.duplicates.length})</h3>
                    <div className="bg-bg-300 rounded-lg p-3 max-h-32 overflow-y-auto">
                      <div className="flex flex-wrap gap-1">
                        {csvResults.duplicates.map((item, index) => (
                          <span key={index} className="bg-yellow-400/20 text-yellow-300 px-2 py-1 rounded text-xs">
                            {item.ticker}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={closeCsvModal}
                  className="px-4 py-2 text-sm font-medium text-text-200 hover:text-text-100"
                >
                  Cancel
                </button>
                {csvResults.successful.length > 0 && (
                  <button
                    type="button"
                    onClick={confirmCsvUpload}
                    disabled={csvConfirmLoading}
                    className="px-4 py-2 bg-primary-100 text-text-100 rounded text-sm hover:bg-primary-200 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {csvConfirmLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-text-100 mr-2 inline-block"></div>
                        Adding...
                      </>
                    ) : (
                      `Add ${csvResults.successful.length} Securities`
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Rankings Table */}
        <div className="mt-4 flow-root">
          <div className="bg-bg-200 rounded-lg shadow-xl overflow-hidden">
            <div className="px-4 py-5 sm:p-6">
              {watchlistData?.as_of_date && (
                <div className="mb-4 text-sm text-text-200">
                  Data as of: {watchlistData.as_of_date}
                </div>
              )}
              
              {sortedData.length === 0 ? (
                <div className="text-center py-12">
                  <h3 className="text-lg font-medium text-text-100 mb-2">No stocks in this watchlist</h3>
                  <p className="text-text-200 mb-4">Add some stocks to get started with your analysis.</p>
                  <button
                    onClick={() => setShowAddModal(true)}
                    className="inline-flex items-center rounded-md bg-primary-100 px-3 py-2 text-sm font-semibold text-text-100 shadow-sm hover:bg-primary-200"
                  >
                    <PlusIcon className="-ml-0.5 mr-1.5 h-5 w-5" aria-hidden="true" />
                    Add Your First Stock
                  </button>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-bg-300 table-fixed">
                    <thead>
                      <tr>
                        <th 
                          onClick={() => requestSort('rank_global')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center">
                            Rank
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('rank');
                                }}
                                onMouseEnter={() => setActiveTooltip('rank')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Rank"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'rank' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm font-normal text-text-100 w-80 left-0 top-full mt-2 border border-bg-400">
                                  {rankDescriptions.rank}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'rank_global' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('ticker')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                        >
                          <div className="flex items-center">
                            Ticker
                            {sortConfig.key === 'ticker' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('company_name')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300" 
                          style={{width: '200px', maxWidth: '200px'}}
                        >
                          <div className="flex items-center">
                            Company Name
                            {sortConfig.key === 'company_name' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('sector')}
                          className="px-3 py-3.5 text-left text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                        >
                          <div className="flex items-center">
                            Sector
                            {sortConfig.key === 'sector' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('price')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                        >
                          <div className="flex items-center justify-end">
                            Price
                            {sortConfig.key === 'price' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('signal_weight')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300"
                        >
                          <div className="flex items-center justify-end">
                            Signal Score
                            {sortConfig.key === 'signal_weight' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        {/* Component Score Headers */}
                        <th 
                          onClick={() => requestSort('component_scores.value')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center justify-end">
                            Value
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('value');
                                }}
                                onMouseEnter={() => setActiveTooltip('value')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Value"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'value' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                  {signalDescriptions.value}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'component_scores.value' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('component_scores.momentum')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center justify-end">
                            Momentum
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('momentum');
                                }}
                                onMouseEnter={() => setActiveTooltip('momentum')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Momentum"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'momentum' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                  {signalDescriptions.momentum}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'component_scores.momentum' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('component_scores.quality')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center justify-end">
                            Quality
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('quality');
                                }}
                                onMouseEnter={() => setActiveTooltip('quality')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Quality"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'quality' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                  {signalDescriptions.quality}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'component_scores.quality' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('component_scores.lowvol')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center justify-end">
                            Low Vol
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('lowvol');
                                }}
                                onMouseEnter={() => setActiveTooltip('lowvol')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Low Vol"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'lowvol' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                  {signalDescriptions.lowvol}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'component_scores.lowvol' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('component_scores.earnings')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center justify-end">
                            Earnings
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('earnings');
                                }}
                                onMouseEnter={() => setActiveTooltip('earnings')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Earnings"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'earnings' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                  {signalDescriptions.earnings}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'component_scores.earnings' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('component_scores.transcripts')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center justify-end">
                            Transcripts
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('transcripts');
                                }}
                                onMouseEnter={() => setActiveTooltip('transcripts')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Transcripts"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'transcripts' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                  {signalDescriptions.transcripts}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'component_scores.transcripts' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th 
                          onClick={() => requestSort('component_scores.innovation')}
                          className="px-3 py-3.5 text-right text-sm font-semibold text-text-100 cursor-pointer hover:bg-bg-300 relative"
                        >
                          <div className="flex items-center justify-end">
                            Innovation
                            <div className="relative">
                              <button
                                type="button"
                                className="ml-1.5 text-text-300 hover:text-text-100 focus:outline-none"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  toggleTooltip('innovation');
                                }}
                                onMouseEnter={() => setActiveTooltip('innovation')}
                                onMouseLeave={() => setActiveTooltip(null)}
                                aria-label="Information about Innovation"
                              >
                                <InformationCircleIcon className="h-4 w-4" aria-hidden="true" />
                              </button>
                              {activeTooltip === 'innovation' && (
                                <div className="absolute z-20 bg-bg-300 p-4 rounded-lg shadow-xl text-sm text-text-100 w-80 right-0 top-full mt-2 border border-bg-400">
                                  {signalDescriptions.innovation}
                                </div>
                              )}
                            </div>
                            {sortConfig.key === 'component_scores.innovation' && (
                              <ArrowsUpDownIcon className="h-4 w-4 ml-1" />
                            )}
                          </div>
                        </th>
                        <th className="px-3 py-3.5 text-center text-sm font-semibold text-text-100">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-bg-300">
                      {sortedData.map((stock) => (
                                              <tr 
                        key={stock.security_id}
                        onClick={() => !stock.is_masked && handleSecurityClick(stock)}
                        className={`${!stock.is_masked ? 'hover:bg-bg-300/50 cursor-pointer' : ''} ${stock.is_masked ? 'bg-bg-300/30' : ''}`}
                      >
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                            {stock.rank_global}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                            {stock.ticker}
                            {stock.is_masked && <LockClosedIcon className="h-3 w-3 inline-block ml-1" />}
                          </td>
                          <td className="px-3 py-4 text-sm text-text-200" style={{width: '200px', maxWidth: '200px'}}>
                            <div className="truncate overflow-hidden whitespace-nowrap" title={stock.name}>
                              {stock.name}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200">
                            {stock.sector}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {stock.price ? (
                              <div className="flex justify-end">
                                <span className="mr-1">$</span>
                                <span>{stock.price}</span>
                              </div>
                            ) : '–'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                            {stock.is_masked ? (
                              <span className="inline-flex items-center rounded-full bg-bg-300 px-2 py-1 text-xs">
                                <LockClosedIcon className="h-3 w-3 mr-1" /> 
                                Subscribe
                              </span>
                            ) : (
                              <span className={`px-2 py-1 rounded-full text-xs ${
                                stock.signal_weight >= 80 ? 'bg-green-400/10 text-green-400' :
                                stock.signal_weight >= 60 ? 'bg-green-300/10 text-green-300' :
                                stock.signal_weight >= 40 ? 'bg-yellow-300/10 text-yellow-300' :
                                stock.signal_weight >= 20 ? 'bg-red-300/10 text-red-300' :
                                'bg-red-400/10 text-red-400'
                              }`}>
                                {formatScore(stock.signal_weight)}
                              </span>
                            )}
                          </td>
                          
                          {/* Component Scores */}
                          <>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                              {formatScore(stock.component_scores.value, stock.is_masked)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                              {formatScore(stock.component_scores.momentum, stock.is_masked)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                              {formatScore(stock.component_scores.quality, stock.is_masked)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                              {formatScore(stock.component_scores.lowvol, stock.is_masked)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                              {formatScore(stock.component_scores.earnings, stock.is_masked)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                              {formatScore(stock.component_scores.transcripts, stock.is_masked)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-right">
                              {formatScore(stock.component_scores.innovation, stock.is_masked)}
                            </td>
                          </>
                          
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-text-200 text-center">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                removeFromWatchlist(stock.security_id);
                              }}
                              className="text-red-400 hover:text-red-300"
                              title="Remove from watchlist"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="w-full bg-bg-200">
        <DisclaimerFooter small />
      </div>
    </div>
  );
} 